import { Map, List } from "immutable";
import * as Immutable from "immutable";
import { singleton } from "logic/singleton";
import generateId from "logic/generateId";
import isDeepEqual from "stores/isDeepEqual";
const isNullish = (o) => o === null || o === void 0;
const _Widget = class _Widget {
  constructor(id, type, config, filter, timerange, query, streams, streamCategories, filters, description, context) {
    this._value = {
      id,
      type,
      config,
      filter: filter === null ? void 0 : filter,
      filters: List(filters),
      timerange,
      query,
      streams,
      stream_categories: streamCategories,
      description,
      context
    };
  }
  get id() {
    return this._value.id;
  }
  get type() {
    return this._value.type;
  }
  get config() {
    return this._value.config;
  }
  get filter() {
    return this._value.filter;
  }
  get filters() {
    return this._value.filters;
  }
  get timerange() {
    return this._value.timerange;
  }
  get query() {
    return this._value.query;
  }
  get streams() {
    return this._value.streams;
  }
  get streamCategories() {
    return this._value.stream_categories;
  }
  get description() {
    return this._value.description;
  }
  withDescription(description) {
    return this.toBuilder().description(description).build();
  }
  get context() {
    return this._value.context;
  }
  // eslint-disable-next-line class-methods-use-this
  get isExportable() {
    return false;
  }
  // eslint-disable-next-line class-methods-use-this
  get returnsAllRecords() {
    return false;
  }
  equals(other) {
    if (other === void 0) {
      return false;
    }
    if (!(other instanceof _Widget)) {
      return false;
    }
    return this.id === other.id && (isNullish(this.filter) && isNullish(other.filter) || isDeepEqual(this.filter, other.filter)) && (isNullish(this.filters) && isNullish(other.filters) || isDeepEqual(this.filters, other.filters)) && isDeepEqual(this.config, other.config) && isDeepEqual(this.timerange, other.timerange) && isDeepEqual(this.query, other.query) && isDeepEqual(this.streams, other.streams) && isDeepEqual(this.streamCategories, other.streamCategories) && isDeepEqual(this.description, other.description) && isDeepEqual(this.context, other.context);
  }
  duplicate(newId) {
    return this.toBuilder().id(newId).build();
  }
  toBuilder() {
    const { id, type, config, filter, filters, timerange, query, streams, stream_categories, description, context } = this._value;
    return new Builder(
      Map({ id, type, config, filter, filters, timerange, query, streams, stream_categories, description, context })
    );
  }
  toJSON() {
    const { id, type, config, filter, filters, timerange, query, streams, stream_categories, description, context } = this._value;
    return {
      id,
      type: type.toLowerCase(),
      config,
      filter,
      filters,
      timerange,
      query,
      streams,
      stream_categories,
      description,
      context
    };
  }
  static fromJSON(value) {
    const { id, type, config, filter, filters, timerange, query, streams, stream_categories, description, context } = value;
    const implementingClass = _Widget.__registrations[type.toLowerCase()];
    if (implementingClass) {
      return implementingClass.fromJSON(value);
    }
    return new _Widget(
      id,
      type,
      config,
      filter,
      timerange,
      query,
      streams,
      stream_categories,
      filters,
      description,
      context
    );
  }
  static empty() {
    return this.builder().build();
  }
  static builder() {
    return new Builder();
  }
  static registerSubtype(type, implementingClass) {
    this.__registrations[type.toLowerCase()] = implementingClass;
  }
};
_Widget.__registrations = {};
let Widget = _Widget;
class Builder {
  constructor(value = Map()) {
    this.value = value;
  }
  id(value) {
    this.value = this.value.set("id", value);
    return this;
  }
  newId() {
    return this.id(generateId());
  }
  type(value) {
    this.value = this.value.set("type", value);
    return this;
  }
  config(value) {
    this.value = this.value.set("config", value);
    return this;
  }
  filter(value) {
    this.value = this.value.set("filter", value);
    return this;
  }
  filters(value) {
    this.value = this.value.set("filters", value ? Immutable.List(value) : value);
    return this;
  }
  timerange(value) {
    this.value = this.value.set("timerange", value);
    return this;
  }
  query(value) {
    this.value = this.value.set("query", value);
    return this;
  }
  streams(value) {
    this.value = this.value.set("streams", value);
    return this;
  }
  streamCategories(value) {
    this.value = this.value.set("stream_categories", value);
    return this;
  }
  description(value) {
    this.value = this.value.set("description", value);
    return this;
  }
  context(value) {
    this.value = this.value.set("context", value);
    return this;
  }
  build() {
    const {
      id,
      type,
      config,
      filter,
      filters,
      timerange,
      query,
      streams,
      stream_categories: streamCategories,
      description,
      context
    } = this.value.toObject();
    return new Widget(
      id,
      type,
      config,
      filter,
      timerange,
      query,
      streams,
      streamCategories,
      filters,
      description,
      context
    );
  }
}
export const widgetAttributesForComparison = [
  "id",
  "config",
  "filter",
  "timerange",
  "query",
  "streams",
  "streamCategories",
  "filters"
];
Widget.Builder = Builder;
const SingletonWidget = singleton("views.logic.widgets.Widget", () => Widget);
export default SingletonWidget;
